import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Library, Plus, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResourceFilters } from "@/components/resources/ResourceFilters";
import { ResourceTable } from "@/components/resources/ResourceTable";
import { ResourceFormDialog } from "@/components/resources/ResourceFormDialog";
import { SendResourceDialog } from "@/components/resources/SendResourceDialog";
import { loadResourceMeta, useResourceMeta } from "@/components/resources/resourceApi";

const EMPTY = { q: "", category: "", status: "", program: "", review: "" };

export default function ResourceLibrary() {
  const meta = useResourceMeta();
  const [params, setParams] = useSearchParams();
  const audience = params.get("audience") === "practitioner" && meta?.perms?.view_practitioner ? "practitioner" : "client";
  const [filters, setFilters] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewCount, setReviewCount] = useState(0);
  const [creating, setCreating] = useState(false);
  const [sendFor, setSendFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clinical-resources", { params: { ...filters, audience } });
      setRows(r.data.items);
    } finally {
      setLoading(false);
    }
  }, [filters, audience]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (!meta?.perms?.approve) return;
    api.get("/clinical-resources", { params: { status: "in_review" } }).then((r) => setReviewCount(r.data.count)).catch(() => {});
  }, [meta, rows]);

  if (!meta) return <div className="text-sm text-zinc-500">Loading…</div>;
  const p = meta.perms;
  return (
    <div className="space-y-6" data-testid="resource-library-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600">Client care</div>
          <h1 className="mt-2 flex items-center gap-3 font-display text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">
            <Library className="h-7 w-7 text-zinc-500" /> Clinical Resource Library
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-600">
            Approved handouts, diaries, guides and protocols. Questionnaires stay in Progress Review and validated scores in Outcome Measures — the library links to them.
          </p>
        </div>
        {p.author && (
          <Button onClick={() => setCreating(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="resource-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add resource
          </Button>
        )}
      </div>

      {p.approve && reviewCount > 0 && (
        <button type="button" onClick={() => setFilters({ ...EMPTY, status: "in_review" })} data-testid="resource-review-queue"
                className="flex w-full items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-left text-sm text-amber-900">
          <ShieldCheck className="h-4 w-4" /> {reviewCount} resource{reviewCount === 1 ? " is" : "s are"} waiting for clinical review
        </button>
      )}

      <Card className="space-y-4 rounded-lg border border-zinc-200 p-5 shadow-none">
        {p.view_practitioner && (
          <Tabs value={audience} onValueChange={(v) => setParams(v === "client" ? {} : { audience: v })}>
            <TabsList data-testid="resource-audience-tabs">
              <TabsTrigger value="client" data-testid="resource-tab-client">Client Resources</TabsTrigger>
              <TabsTrigger value="practitioner" data-testid="resource-tab-practitioner">Practitioner Resources</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <ResourceFilters meta={meta} filters={filters} onChange={setFilters} showStatus={!p.reception} />
        <ResourceTable rows={rows} meta={meta} loading={loading} onSend={setSendFor} />
      </Card>

      {creating && (
        <ResourceFormDialog meta={meta} defaultAudience={audience} onClose={() => setCreating(false)}
                            onSaved={() => { setCreating(false); loadResourceMeta(true); load(); }} />
      )}
      {sendFor && <SendResourceDialog resource={sendFor} source={{ kind: "library" }} onClose={() => setSendFor(null)} onSent={load} />}
    </div>
  );
}
